/**
 * Adaptive screener step (Phase 3).
 *
 * Renders the composed adaptive-screener sections as a one-open-at-a-time rail
 * (PRD §8.1): the whole planned list is visible with a one-line reason under
 * each, only one section's questions expanded at a time, each administered with
 * the existing `QuestionnaireRenderer`.
 *
 * Adaptive behaviors wired here:
 * - Trigger-add (§8.4 / D9): a positive PHQ-9 self-harm answer inserts the
 *   suicide-risk + safety-plan sections directly after depression.
 * - Anxiety short-check reveal: a positive GAD-2 (≥3) opens the full GAD-7.
 * - Persistent, acknowledged safety notices (§8.5 / D13): a positive
 *   suicide-risk screen surfaces a rail-level escalate-now notice that persists
 *   regardless of which section is open and must be acknowledged; a positive
 *   drug screen keeps its follow-up instruction on screen.
 * - Settlement / refusal (§8.6 / D8): every section settles as answered, a
 *   patient decline (kept, no score), or an advocate-judgement "not clinically
 *   appropriate"; one action declines the rest; declines can be undone; and the
 *   rail surfaces whether the screening may be recorded (C6 + D13).
 *
 * Still to land: the record-submit gate itself (needs form/persistence
 * integration) and on-page instrument attribution.
 *
 * @agentic connections
 */

import {
  Accordion,
  Alert,
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  Stack,
  Text,
} from "@mantine/core";
import { IconAlertTriangle } from "@tabler/icons-react";
import { useEffect, useMemo, useState } from "react";

import {
  QUESTIONNAIRE_URLS,
  getQuestionnaireItems,
  type QuestionItem,
} from "@benchmark/core/constants";
import {
  insertTriggeredSections,
  isExplicitSettlement,
  isSelfHarmPositive,
  patientDeclineOption,
  recordBlockedReason,
  shouldRevealAnxietyFullSetFromState,
  type AdaptiveSection,
  type AdaptiveSectionKey,
  type AdaptiveSectionStatus,
} from "@benchmark/core/services";
import type { EncounterQuestionnaireResponseEntry } from "@benchmark/core/types";
import { QuestionnaireRenderer } from "@/components/shared/forms/QuestionnaireRenderer";
import {
  AdaptiveScreenerWayfinding,
  sectionTitle,
} from "@/components/domain/encounter/shared/AdaptiveScreenerWayfinding";

const SETTLEMENT_LABELS: Record<AdaptiveSectionStatus, string> = {
  pending: "Not settled",
  answered: "Answered",
  "declined-remaining": "Patient declined the rest",
  "declined-section": "Patient declined",
  "not-administered-clinical": "Not administered — clinical judgement",
};
const EMPTY_READ_ONLY_QUESTIONNAIRE_URLS: ReadonlySet<string> = new Set();
const EMPTY_DUPLICATE_SCORE_QUESTIONNAIRE_URLS: ReadonlySet<string> = new Set();

const urlForKey = (key: keyof typeof QUESTIONNAIRE_URLS): string =>
  QUESTIONNAIRE_URLS[key];

const sumScore = (entry?: EncounterQuestionnaireResponseEntry): number =>
  (entry?.responses ?? []).reduce((total, r) => total + (r.value ?? 0), 0);

const isAnsweredResponse = (response: {
  value?: number;
  valueString?: string;
}): boolean =>
  response.value !== undefined || response.valueString !== undefined;

/** Counts leaf choice/string items, flattening nested groups (e.g. safety plan). */
const countAnswerableItems = (items: readonly QuestionItem[]): number =>
  items.reduce((count, item) => {
    if (item.type === "group" && item.item) {
      return count + countAnswerableItems(item.item);
    }
    if (item.type !== "group") {
      return count + 1;
    }
    return count;
  }, 0);

const anyPositive = (
  entry: EncounterQuestionnaireResponseEntry | undefined,
  linkId?: string,
): boolean =>
  (entry?.responses ?? []).some(
    (r) => (!linkId || r.linkId === linkId) && (r.value ?? 0) > 0,
  );

/** Props for {@link AdaptiveScreenerStep}. @agentic connections */
export type AdaptiveScreenerStepProps = {
  /** Composed, ordered base sections from `composeAdaptiveScreener`. */
  sections: readonly AdaptiveSection[];
  /** In-form responses keyed by questionnaire URL. */
  questionnaireResponses: Record<string, EncounterQuestionnaireResponseEntry>;
  /**
   * Explicit section settlements (declines / clinician-judgement) restored from
   * the form so they survive the rail remounting and are not reset (D8).
   */
  initialSettlements?: Partial<
    Record<AdaptiveSectionKey, AdaptiveSectionStatus>
  >;
  /** Called when a section's responses change (keyed by questionnaire URL). */
  onResponsesChange: (
    url: string,
    entry: EncounterQuestionnaireResponseEntry,
  ) => void;
  /**
   * The suicide-risk acknowledgement (D13). Controlled by the parent (stored on
   * the form) so it survives the rail unmounting when the screener is toggled.
   */
  acknowledged?: boolean;
  /** Called when the suicide-risk acknowledgement (D13) is toggled. */
  onSuicideRiskAcknowledgedChange?: (acknowledged: boolean) => void;
  /** Called when record-eligibility changes (C6 + D13) so the form can gate submit. */
  onRecordEligibilityChange?: (canRecord: boolean) => void;
  /**
   * Called when the effective settlement of each displayed section changes,
   * keyed by the settlement-relevant questionnaire URL, so the form can persist
   * partial answers and declines (D8).
   */
  onSettlementsChange?: (byUrl: Record<string, AdaptiveSectionStatus>) => void;
  /** Instruments with ambiguous persisted artifacts; displayed but immutable. */
  readOnlyQuestionnaireUrls?: ReadonlySet<string>;
  /** Duplicate-score instruments that may be omitted from record eligibility. */
  duplicateScoreQuestionnaireUrls?: ReadonlySet<string>;
  disabled?: boolean;
};

/**
 * The adaptive screener rail with its live adaptive behaviors and settlement.
 *
 * @agentic connections
 */
export const AdaptiveScreenerStep = ({
  sections,
  questionnaireResponses,
  initialSettlements,
  onResponsesChange,
  acknowledged = false,
  onSuicideRiskAcknowledgedChange,
  onRecordEligibilityChange,
  onSettlementsChange,
  readOnlyQuestionnaireUrls = EMPTY_READ_ONLY_QUESTIONNAIRE_URLS,
  duplicateScoreQuestionnaireUrls = EMPTY_DUPLICATE_SCORE_QUESTIONNAIRE_URLS,
  disabled = false,
}: AdaptiveScreenerStepProps) => {
  const [openSection, setOpenSection] = useState<string | null>(
    sections[0]?.key ?? null,
  );
  // Only explicit decline / clinician-judgement decisions are restored as
  // settlements. `pending` and `answered` are always derived from the live
  // responses (see `effectiveStatus`), so seeding them here would make a section
  // read as explicitly settled after a remount — hiding the decline controls and
  // showing a spurious "Not settled" + Undo — even though nothing was decided.
  const [statuses, setStatuses] = useState<
    Partial<Record<AdaptiveSectionKey, AdaptiveSectionStatus>>
  >(() => {
    const seed: Partial<Record<AdaptiveSectionKey, AdaptiveSectionStatus>> = {};
    for (const [key, status] of Object.entries(initialSettlements ?? {})) {
      if (status && isExplicitSettlement(status)) {
        seed[key as AdaptiveSectionKey] = status;
      }
    }
    return seed;
  });

  // Trigger-add (§8.4 / D9): a positive PHQ-9 self-harm answer adds the
  // suicide-risk + safety-plan sections directly after depression.
  const phq9 = questionnaireResponses[urlForKey("PHQ9")];
  const selfHarmPositive = isSelfHarmPositive(phq9?.responses ?? []);
  const displayed = insertTriggeredSections(sections, { selfHarmPositive });

  // Persistent safety-notice conditions (§8.5), evaluated at the rail level.
  // Gated on today's self-harm answer (E6): once self-harm is reverted the
  // suicide-risk section is gone, so stale C-SSRS answers must not read positive.
  const cssrsPositive =
    selfHarmPositive &&
    anyPositive(questionnaireResponses[urlForKey("C_SSRS")]);
  const drugPositive = anyPositive(
    questionnaireResponses[urlForKey("DRUG_USE")],
    "drug-use-1",
  );

  // Anxiety short-check reveal: a positive GAD-2 opens the full GAD-7; existing
  // GAD-7 answers re-open it on draft resume when GAD-2 was not persisted.
  const gad7Entry = questionnaireResponses[urlForKey("GAD7")];
  const hasGad7Responses = (gad7Entry?.responses ?? []).some(
    (response) => response.value !== undefined,
  );
  const revealAnxietyFull = shouldRevealAnxietyFullSetFromState({
    gad2Score: sumScore(questionnaireResponses[urlForKey("GAD2")]),
    hasGad7Responses,
  });

  // When memory says a short-check section opened in full at an earlier visit,
  // it is asked in full today regardless of today's short check (E2/E3): the
  // anxiety section administers GAD-7 directly instead of gating it on GAD-2.
  const isAnxietyFullByMemory = (section: AdaptiveSection): boolean =>
    section.key === "anxiety" && section.gating === "full-by-memory";

  // The instrument that carries the section's answers for settlement/decline
  // detection. For memory-opened anxiety that is GAD-7 (GAD-2 is not shown).
  const settlementUrl = (section: AdaptiveSection): string =>
    isAnxietyFullByMemory(section)
      ? urlForKey("GAD7")
      : urlForKey(section.instruments[0] as keyof typeof QUESTIONNAIRE_URLS);

  // Every instrument actually administered for a section — so each partial or
  // declined instrument is recorded, not just the section's primary (D8). Anxiety
  // administers GAD-7 alongside GAD-2 once the short check opens the full set.
  const administeredUrls = (section: AdaptiveSection): string[] => {
    if (section.key === "anxiety") {
      if (isAnxietyFullByMemory(section)) return [urlForKey("GAD7")];
      return revealAnxietyFull
        ? [urlForKey("GAD2"), urlForKey("GAD7")]
        : [urlForKey("GAD2")];
    }
    return [
      urlForKey(section.instruments[0] as keyof typeof QUESTIONNAIRE_URLS),
    ];
  };
  const isSectionReadOnly = (section: AdaptiveSection): boolean =>
    administeredUrls(section).some((url) => readOnlyQuestionnaireUrls.has(url));
  const requiredUrlsForSection = (section: AdaptiveSection): string[] => {
    const urls = administeredUrls(section);
    return section.key === "suicide-risk"
      ? urls
      : urls.filter((url) => !duplicateScoreQuestionnaireUrls.has(url));
  };

  const isSectionAnswered = (section: AdaptiveSection): boolean => {
    const completed = (key: keyof typeof QUESTIONNAIRE_URLS) =>
      questionnaireResponses[urlForKey(key)]?.completed === true;
    if (section.key === "anxiety") {
      if (isAnxietyFullByMemory(section)) {
        return completed("GAD7");
      }
      return completed("GAD2") && (!revealAnxietyFull || completed("GAD7"));
    }
    return (
      questionnaireResponses[
        urlForKey(section.instruments[0] as keyof typeof QUESTIONNAIRE_URLS)
      ]?.completed === true
    );
  };

  const effectiveStatus = (section: AdaptiveSection): AdaptiveSectionStatus =>
    statuses[section.key] ??
    (isSectionAnswered(section) ? "answered" : "pending");

  const settle = (key: AdaptiveSectionKey, status: AdaptiveSectionStatus) =>
    setStatuses((prev) => ({ ...prev, [key]: status }));

  const undo = (key: AdaptiveSectionKey) =>
    setStatuses((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });

  const declineRest = () =>
    setStatuses((prev) => {
      const next = { ...prev };
      for (const section of displayed) {
        if (isSectionReadOnly(section)) continue;
        if (effectiveStatus(section) !== "pending") continue;
        const url = settlementUrl(section);
        const hasAnswer =
          (questionnaireResponses[url]?.responses.length ?? 0) > 0;
        next[section.key] = patientDeclineOption(hasAnswer);
      }
      return next;
    });

  const recordStatus = (
    section: AdaptiveSection,
  ): AdaptiveSectionStatus | null => {
    const requiredUrls = requiredUrlsForSection(section);
    if (requiredUrls.length === 0) return null;
    if (
      requiredUrls.some(
        (url) =>
          readOnlyQuestionnaireUrls.has(url) &&
          (!duplicateScoreQuestionnaireUrls.has(url) ||
            section.key === "suicide-risk"),
      )
    ) {
      return "pending";
    }
    const explicitStatus = statuses[section.key];
    if (explicitStatus !== undefined) return explicitStatus;
    return requiredUrls.every(
      (url) => questionnaireResponses[url]?.completed === true,
    )
      ? "answered"
      : "pending";
  };
  const requiredStatuses = displayed.flatMap((section) => {
    const status = recordStatus(section);
    return status === null ? [] : [status];
  });
  const blockedReason =
    requiredStatuses.length === 0 && displayed.length > 0
      ? cssrsPositive && !acknowledged
        ? "suicide-risk-unacknowledged"
        : null
      : recordBlockedReason({
          statuses: requiredStatuses,
          suicideRiskPositive: cssrsPositive,
          suicideRiskAcknowledged: acknowledged,
        });
  const canRecord = blockedReason === null;

  // Surface record-eligibility to the parent so it can gate the encounter submit.
  useEffect(() => {
    onRecordEligibilityChange?.(canRecord);
  }, [canRecord, onRecordEligibilityChange]);

  // Surface each displayed section's effective settlement (keyed by the
  // settlement-relevant url) so the form can persist partial answers and
  // declines (D8). The suicide-risk + safety-plan sections are trigger-added and
  // also reported so their decline/answer is recorded.
  const settlementsSignature = displayed
    .map(
      (section) =>
        `${administeredUrls(section).join(",")}:${effectiveStatus(section)}`,
    )
    .join("|");
  useEffect(() => {
    if (!onSettlementsChange) return;
    const byUrl: Record<string, AdaptiveSectionStatus> = {};
    for (const section of displayed) {
      const status = effectiveStatus(section);
      // Never persist the transient `pending` default: it is not a decision, and
      // rehydrating it on a remount would make an unsettled section look settled
      // (blocking decline/undo) and the save path treat it as a decline (D8).
      if (status === "pending") continue;
      for (const url of administeredUrls(section)) {
        byUrl[url] = status;
      }
    }
    onSettlementsChange(byUrl);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settlementsSignature, onSettlementsChange]);

  const writableUnsettledCount = displayed.filter(
    (section) =>
      !isSectionReadOnly(section) && effectiveStatus(section) === "pending",
  ).length;
  const requiredUnsettledCount = requiredStatuses.filter(
    (status) => status === "pending",
  ).length;

  const settledCount = useMemo(
    () => displayed.filter((s) => effectiveStatus(s) !== "pending").length,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayed, statuses, questionnaireResponses, revealAnxietyFull],
  );

  const answeredQuestionCount = useMemo(
    () =>
      displayed.reduce((count, section) => {
        for (const url of administeredUrls(section)) {
          const entry = questionnaireResponses[url];
          count += (entry?.responses ?? []).filter(isAnsweredResponse).length;
        }
        return count;
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayed, questionnaireResponses, revealAnxietyFull],
  );

  const approxQuestionCount = useMemo(
    () =>
      displayed.reduce((count, section) => {
        for (const url of administeredUrls(section)) {
          count += countAnswerableItems(getQuestionnaireItems(url) ?? []);
        }
        return count;
      }, 0),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayed, revealAnxietyFull],
  );

  const activeSectionKey =
    openSection && displayed.some((section) => section.key === openSection)
      ? (openSection as AdaptiveSectionKey)
      : null;

  const jumpToSection = (key: AdaptiveSectionKey) => {
    setOpenSection(key);
    document
      .querySelector(`[data-testid="adaptive-section-${key}"]`)
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  if (displayed.length === 0) {
    return null;
  }

  const renderInstrument = (url: string, extraReason?: string) => {
    const responses = questionnaireResponses[url] ?? {
      responses: [],
      completed: false,
    };
    return (
      <Stack gap="xs" data-testid={`adaptive-instrument-${url}`}>
        {extraReason && (
          <Text size="xs" c="dimmed">
            {extraReason}
          </Text>
        )}
        <QuestionnaireRenderer
          questionnaireUrl={url}
          responses={responses}
          disabled={disabled || readOnlyQuestionnaireUrls.has(url)}
          onChange={(newResponses) => {
            if (!readOnlyQuestionnaireUrls.has(url)) {
              onResponsesChange(url, newResponses);
            }
          }}
          showScore={responses.completed}
          showAttribution
        />
      </Stack>
    );
  };

  const renderSettlement = (section: AdaptiveSection) => {
    const status = effectiveStatus(section);
    const isExplicit = statuses[section.key] !== undefined;
    const url = settlementUrl(section);
    const hasAnswer = (questionnaireResponses[url]?.responses.length ?? 0) > 0;
    const declineStatus = patientDeclineOption(hasAnswer);
    const sectionDisabled = disabled || isSectionReadOnly(section);

    if (status === "answered") {
      return (
        <Badge color="green" variant="light">
          {SETTLEMENT_LABELS.answered}
        </Badge>
      );
    }

    if (isExplicit) {
      return (
        <Group gap="sm">
          <Badge color="gray" variant="light">
            {SETTLEMENT_LABELS[status]}
          </Badge>
          <Button
            size="compact-xs"
            variant="subtle"
            disabled={sectionDisabled}
            data-testid={`adaptive-undo-${section.key}`}
            onClick={() => undo(section.key)}
          >
            Undo
          </Button>
        </Group>
      );
    }

    return (
      <Group gap="sm">
        <Button
          size="compact-xs"
          variant="light"
          color="gray"
          disabled={sectionDisabled}
          data-testid={`adaptive-decline-${section.key}`}
          onClick={() => settle(section.key, declineStatus)}
        >
          {declineStatus === "declined-remaining"
            ? "Patient declines the rest"
            : "Patient declines this section"}
        </Button>
        <Button
          size="compact-xs"
          variant="subtle"
          color="gray"
          disabled={sectionDisabled}
          data-testid={`adaptive-not-appropriate-${section.key}`}
          onClick={() => settle(section.key, "not-administered-clinical")}
        >
          Not clinically appropriate
        </Button>
      </Group>
    );
  };

  return (
    <Stack gap="md">
      {/* Persistent safety notices (§8.5 / D13). */}
      {cssrsPositive && (
        <Alert
          color="red"
          radius="md"
          variant="filled"
          icon={<IconAlertTriangle size={18} />}
          data-testid="adaptive-cssrs-escalate-notice"
        >
          <Text fw={700} size="sm" mb={4}>
            Escalate now — positive suicide-risk screen
          </Text>
          <Text size="sm" mb="xs">
            Follow the operational escalation protocol before the call ends.
            This screening cannot be recorded until you acknowledge it.
          </Text>
          <Checkbox
            checked={acknowledged}
            disabled={
              disabled ||
              readOnlyQuestionnaireUrls.has(QUESTIONNAIRE_URLS.C_SSRS)
            }
            data-testid="adaptive-cssrs-acknowledge"
            label="I have initiated or completed the escalation protocol"
            onChange={(event) =>
              onSuicideRiskAcknowledgedChange?.(event.currentTarget.checked)
            }
          />
        </Alert>
      )}

      {drugPositive && (
        <Alert
          color="orange"
          radius="md"
          variant="light"
          icon={<IconAlertTriangle size={18} />}
          data-testid="adaptive-drug-followup-notice"
        >
          <Text size="sm">
            Set up a follow-up with the patient before the call ends.
          </Text>
        </Alert>
      )}

      <Group align="flex-start" wrap="nowrap" gap="lg">
        <AdaptiveScreenerWayfinding
          sections={displayed}
          activeSectionKey={activeSectionKey}
          onJump={jumpToSection}
          settledCount={settledCount}
          totalCount={displayed.length}
          answeredQuestionCount={answeredQuestionCount}
          approxQuestionCount={approxQuestionCount}
          statusFor={effectiveStatus}
        />

        <Box style={{ flex: 1, minWidth: 0 }}>
          <Accordion
            value={openSection}
            onChange={setOpenSection}
            variant="separated"
            radius="md"
          >
            {displayed.map((section) => {
              const primaryUrl = urlForKey(
                section.instruments[0] as keyof typeof QUESTIONNAIRE_URLS,
              );
              return (
                <Accordion.Item key={section.key} value={section.key}>
                  <Accordion.Control
                    data-testid={`adaptive-section-${section.key}`}
                  >
                    <Group justify="space-between" wrap="nowrap" pr="sm">
                      <Stack gap={2}>
                        <Text fw={600} size="sm">
                          {sectionTitle(section)}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {section.reason}
                        </Text>
                      </Stack>
                      {effectiveStatus(section) !== "pending" && (
                        <Badge
                          color={
                            effectiveStatus(section) === "answered"
                              ? "green"
                              : "gray"
                          }
                          variant="light"
                          size="sm"
                        >
                          {SETTLEMENT_LABELS[effectiveStatus(section)]}
                        </Badge>
                      )}
                    </Group>
                  </Accordion.Control>
                  <Accordion.Panel>
                    <Stack gap="md">
                      {section.key === "anxiety" ? (
                        isAnxietyFullByMemory(section) ? (
                          <Stack gap="md">
                            {renderInstrument(
                              urlForKey("GAD7"),
                              "Asked in full — opened at an earlier visit.",
                            )}
                          </Stack>
                        ) : (
                          <Stack gap="md">
                            {renderInstrument(urlForKey("GAD2"))}
                            {revealAnxietyFull &&
                              renderInstrument(
                                urlForKey("GAD7"),
                                "Opened because the short check was positive.",
                              )}
                          </Stack>
                        )
                      ) : (
                        renderInstrument(primaryUrl)
                      )}
                      {renderSettlement(section)}
                    </Stack>
                  </Accordion.Panel>
                </Accordion.Item>
              );
            })}
          </Accordion>
        </Box>
      </Group>

      <Group justify="space-between">
        <Button
          size="compact-sm"
          variant="subtle"
          color="gray"
          disabled={disabled || writableUnsettledCount === 0}
          data-testid="adaptive-decline-rest"
          onClick={declineRest}
        >
          Patient declines the rest of the screening
        </Button>
        <Text
          size="sm"
          c={canRecord ? "green.7" : "orange.7"}
          fw={600}
          data-testid="adaptive-record-status"
        >
          {blockedReason === "unsettled-sections"
            ? `${requiredUnsettledCount} section${requiredUnsettledCount === 1 ? "" : "s"} still to settle before recording`
            : blockedReason === "suicide-risk-unacknowledged"
              ? "Acknowledge the suicide-risk notice before recording"
              : blockedReason === "no-sections"
                ? "No sections to record"
                : "All sections settled — ready to record"}
        </Text>
      </Group>
    </Stack>
  );
};
